import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, ChevronUp, Filter, RefreshCw, Clock, Calendar, ExternalLink, Share2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchUpcomingEvents, fetchAllEvents } from "@/lib/api";
import AddToCalendarButton from "./AddToCalendarButton";
import ShareEventCard from "./ShareEventCard";

const CATEGORY_CONFIG = {
  retrograde: { label: "Retrograde", color: "#EF4444", bgColor: "rgba(239, 68, 68, 0.15)" },
  direct: { label: "Direct Station", color: "#22C55E", bgColor: "rgba(34, 197, 94, 0.15)" },
  ingress: { label: "Sign Change", color: "#3B82F6", bgColor: "rgba(59, 130, 246, 0.15)" },
  new_moon: { label: "New Moon", color: "#6B7280", bgColor: "rgba(107, 114, 128, 0.15)" },
  full_moon: { label: "Full Moon", color: "#FCD34D", bgColor: "rgba(252, 211, 77, 0.15)" },
  solar_eclipse: { label: "Solar Eclipse", color: "#7C3AED", bgColor: "rgba(124, 58, 237, 0.15)" },
  lunar_eclipse: { label: "Lunar Eclipse", color: "#DC2626", bgColor: "rgba(220, 38, 38, 0.15)" },
};

const FILTER_OPTIONS = [
  { value: null, label: "All Events" },
  { value: "retrograde,direct", label: "Retrogrades & Direct" },
  { value: "ingress", label: "Sign Changes" },
  { value: "new_moon,full_moon", label: "Moon Phases" },
  { value: "solar_eclipse,lunar_eclipse", label: "Eclipses" },
];

function formatCountdown(targetDate) {
  const now = new Date();
  const target = new Date(targetDate);
  const diff = target - now;

  if (diff <= 0) return "Now";
  if (diff < 60000) return "Less than a minute";

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (days >= 1) {
    const remainingHours = Math.floor((diff % 86400000) / 3600000);
    return `In ${days} day${days !== 1 ? "s" : ""}, ${remainingHours} hr${remainingHours !== 1 ? "s" : ""}`;
  }
  if (hours >= 1) {
    const remainingMins = Math.floor((diff % 3600000) / 60000);
    return `In ${hours} hr${hours !== 1 ? "s" : ""}, ${remainingMins} min`;
  }
  return `In ${minutes} min`;
}

function formatLocalDateTime(utcString) {
  const date = new Date(utcString);
  const options = {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  return date.toLocaleString(undefined, options);
}

function EventCard({ event, isExpanded, onToggle, onShare }) {
  const [countdown, setCountdown] = useState(() => formatCountdown(event.timestamp_utc));
  const config = CATEGORY_CONFIG[event.category] || CATEGORY_CONFIG.ingress;

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(formatCountdown(event.timestamp_utc));
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [event.timestamp_utc]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="rounded-lg overflow-hidden"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
      data-testid={`event-card-${event.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full p-4 sm:p-5 text-left flex items-start gap-4 hover:bg-white/[0.02] transition-colors"
        data-testid={`event-toggle-${event.id}`}
      >
        {/* Icon */}
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-lg"
          style={{ background: config.bgColor }}
        >
          <span style={{ color: config.color }}>{event.icon}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Category Badge */}
          <span
            className="inline-block text-[10px] tracking-wider uppercase px-2 py-0.5 rounded mb-1.5"
            style={{ background: config.bgColor, color: config.color }}
          >
            {config.label}
          </span>

          {/* Title */}
          <h3 className="text-white/90 text-base font-medium leading-tight mb-1" data-testid={`event-title-${event.id}`}>
            {event.title}
          </h3>

          {/* Subtitle */}
          {event.subtitle && (
            <p className="text-white/40 text-sm mb-2">{event.subtitle}</p>
          )}

          {/* Countdown and Date */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="text-amber-400/80 font-medium" data-testid={`event-countdown-${event.id}`}>
              <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
              {countdown}
            </span>
            <span className="text-white/30">
              <Calendar className="w-3 h-3 inline mr-1 -mt-0.5" />
              {formatLocalDateTime(event.timestamp_utc)}
            </span>
          </div>
        </div>

        {/* Expand/Collapse */}
        <div className="flex-shrink-0 text-white/30">
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-0">
              <div className="border-t border-white/5 pt-4">
                <h4 className="text-white/50 text-xs tracking-wider uppercase mb-2">What this means</h4>
                <p className="text-white/70 text-sm leading-relaxed" data-testid={`event-interpretation-${event.id}`}>
                  {event.interpretation}
                </p>
                <p className="text-white/30 text-[11px] mt-3 italic">
                  General astrological themes — not personalised predictions.
                </p>
                
                {/* Add to Calendar */}
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-3">
                  <AddToCalendarButton event={event} variant="default" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShare(event);
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors text-sm"
                    data-testid={`event-share-${event.id}`}
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function UpcomingEvents({ showTitle = true, initialLimit = 6, maxDays = 90 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [shareEvent, setShareEvent] = useState(null);
  const refreshTimeoutRef = useRef(null);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const limit = showAll ? 0 : initialLimit;
      const response = showAll
        ? await fetchAllEvents(maxDays, filter)
        : await fetchUpcomingEvents(limit, maxDays, filter);

      // Filter by category on client side if filter contains multiple values
      let filteredEvents = response.events;
      if (filter && filter.includes(",")) {
        const categories = filter.split(",");
        filteredEvents = response.events.filter((e) => categories.includes(e.category));
      }

      setEvents(filteredEvents);
      setLastUpdated(response.generated_at);
    } catch (err) {
      console.error("Failed to load events:", err);
      setError("Unable to load astrological events. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [filter, showAll, initialLimit, maxDays]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Refresh on tab visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadEvents();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [loadEvents]);

  // Auto-remove past events every minute
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().toISOString();
      setEvents((prev) => prev.filter((e) => e.timestamp_utc > now));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleToggle = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleShare = (event) => {
    setShareEvent(event);
  };

  const handleFilterChange = (value) => {
    setFilter(value);
    setShowFilters(false);
    setShowAll(false);
  };

  if (loading && events.length === 0) {
    return (
      <div className="py-8" data-testid="events-loading">
        <div className="flex flex-col items-center gap-3">
          <img src="/logo.png" alt="Loading" className="h-8 w-auto logo-pulse" />
          <p className="text-white/30 text-sm">Calculating celestial alignments...</p>
        </div>
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div className="py-8 text-center" data-testid="events-error">
        <p className="text-white/40 text-sm mb-3">{error}</p>
        <button
          onClick={loadEvents}
          className="text-amber-400/70 hover:text-amber-400 text-sm flex items-center gap-1 mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="upcoming-events-list">
      {/* Header */}
      {showTitle && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl sm:text-4xl font-light text-white/90">Upcoming Celestial Events</h2>
          <div className="flex items-center gap-2">
            {/* Filter Button */}
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white/50 hover:text-white/70 hover:bg-white/5 transition-colors text-sm"
                data-testid="events-filter-btn"
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filter</span>
              </button>

              {/* Filter Dropdown */}
              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="absolute right-0 top-full mt-2 w-52 rounded-lg py-2 z-50"
                    style={{ background: "#151515", border: "1px solid rgba(255,255,255,0.1)" }}
                  >
                    {FILTER_OPTIONS.map((opt) => (
                      <button
                        key={opt.value || "all"}
                        onClick={() => handleFilterChange(opt.value)}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          filter === opt.value
                            ? "text-white bg-white/5"
                            : "text-white/60 hover:text-white hover:bg-white/5"
                        }`}
                        data-testid={`filter-option-${opt.value || "all"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}

      {/* Active Filter Indicator */}
      {filter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-white/40">Showing:</span>
          <span className="text-amber-400/80">
            {FILTER_OPTIONS.find((o) => o.value === filter)?.label}
          </span>
          <button
            onClick={() => setFilter(null)}
            className="text-white/30 hover:text-white/60 text-xs ml-1"
          >
            (clear)
          </button>
        </div>
      )}

      {/* Events Grid */}
      {events.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-white/30 text-sm">
            No upcoming events found for the selected filter.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AnimatePresence mode="popLayout">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                isExpanded={expandedId === event.id}
                onToggle={() => handleToggle(event.id)}
                onShare={handleShare}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* View More / View Less */}
      {!showAll && events.length >= initialLimit && (
        <div className="text-center pt-4">
          <button
            onClick={() => setShowAll(true)}
            className="text-white/50 hover:text-white/70 text-sm flex items-center gap-1 mx-auto transition-colors"
            data-testid="events-view-more"
          >
            View more events
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {showAll && (
        <div className="text-center pt-4">
          <button
            onClick={() => setShowAll(false)}
            className="text-white/50 hover:text-white/70 text-sm flex items-center gap-1 mx-auto transition-colors"
            data-testid="events-view-less"
          >
            Show fewer events
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Last Updated Footer */}
      {lastUpdated && (
        <div className="text-center pt-2">
          <p className="text-white/20 text-[11px]">
            Tropical zodiac · Sidereal nakshatras · True Lahiri
          </p>
        </div>
      )}

      {/* Share Modal */}
      <AnimatePresence>
        {shareEvent && (
          <ShareEventCard
            event={shareEvent}
            onClose={() => setShareEvent(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
